/**
 * Today's work list as the approved prototype (2026-09-30, Action desk) describes it, as pure functions: one uniform
 * row anatomy for review items and assignments, the "prerequisites first" order, the four tabs' counts (narrowed to a
 * focused company), the blocked-results relationship and the suggested next step. Screens/Today.tsx renders them.
 *
 * Order: review items first by the server's AIC-372 priority (0 holds a result's approval, 1 the vendor is waiting,
 * 2 a figure, 3 other; ties keep the server's order), then the assignment actions (spec to approve, invite draft to
 * create, draft to send, follow-up, results). A results row with an open item that blocks_results comes last as
 * "Review prerequisite", linked to the item by its assignment_id.
 */
import { periodCodeLabel } from "@swtracker/core/period-label";
import { sendUndetectedDays, TODAY_STATE_OF } from "@swtracker/core/portal/nextStep";
import type { Queue } from "../api/schemas.ts";
import { reviewKind } from "../components/reviewKinds.tsx";
import { needsYou, stateOf, type StateKey } from "../components/status.tsx";

export type QueueReview = Queue["open_review_items"][number];
export type QueueAssignment = Queue["assignments"][number];
export type Tab = "you" | "assignments" | "reviews" | "wait";
export const TABS: readonly { id: Tab; label: string }[] = [
  { id: "you", label: "Needs you" }, { id: "assignments", label: "Assignments" }, { id: "reviews", label: "Reviews" }, { id: "wait", label: "Waiting" },
];

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
export { plural };

/** The row's initial tile: the company's first letter or digit. */
export const initialOf = (company: string): string => (/[\p{L}\p{N}]/u.exec(company)?.[0] ?? "·").toUpperCase();

/** "2026H1" → "H1 2026". */
export const cycleLabel = (id: string): string => periodCodeLabel(id);

/** A review summary's row copy (AIC-372 title / issue_label / summary), falling back to the kind and reason for an older server. */
export function reviewCopy(r: QueueReview): { title: string; meta: string; line: string; verb: string } {
  const kind = reviewKind(r.kind).label;
  const meta = r.issue_label ?? kind;
  return { title: r.title ?? kind, meta, line: r.summary ?? r.reason, verb: meta === "Vendor question" ? "Review question" : "Review item" };
}

/** Review items, prerequisites first (lower priority first; ties keep the server's order). */
export function byPriority<T extends { priority?: number }>(items: readonly T[]): T[] {
  return items.map((r, i) => ({ r, i })).sort((x, y) => (x.r.priority ?? 3) - (y.r.priority ?? 3) || x.i - y.i).map((x) => x.r);
}

/**
 * The open items that hold this assignment's result approval: matched by the item's assignment_id; only an older
 * server, which sends none, falls back to the company.
 */
export const blockingFor = (a: { id: string; company: string }, reviews: readonly QueueReview[]) =>
  reviews.filter((r) => r.blocks_results === true && (r.assignment_id ? r.assignment_id === a.id : r.company === a.company));

const RANK: Partial<Record<StateKey, number>> = { specReady: 0, specOk: 1, drafted: 2, follow: 3, results: 4 };
/** Assignment actions in the prototype's order; a blocked results row goes after every other action. */
export function orderAssignments<T extends { state: StateKey | null }>(rows: readonly T[], blocked: (r: T) => boolean): T[] {
  const rank = (r: T) => (blocked(r) ? 9 : r.state ? RANK[r.state] ?? 5 : 5);
  return rows.map((r, i) => ({ r, i })).sort((x, y) => rank(x.r) - rank(y.r) || x.i - y.i).map((x) => x.r);
}

/** Whether a row is an action for the viewer: a needs-you state, or a spec-approved draft on their own queue. */
export const isAction = (state: StateKey | null, draftable: boolean) => needsYou(state) || (state === "specOk" && draftable);

/** The display statuses the server should return for a tab: the actions, or everything else (Waiting). */
export function tabStatuses(tab: Tab, draftable: boolean): string[] {
  const actions = Object.keys(TODAY_STATE_OF).filter((s) => isAction(stateOf(s), draftable));
  return tab === "wait" ? Object.keys(TODAY_STATE_OF).filter((s) => !actions.includes(s)) : actions;
}

export interface TodayCounts { readonly actions: number; readonly reviews: number; readonly waiting: number }

/**
 * The tab counts: Needs you = actions + reviews; Assignments = actions only; Reviews; Waiting. From the server's
 * full-scope totals (any page, any filter); a focused company narrows them to its by_company entry. A company's
 * spec-approved drafts are counted from the loaded rows, which are that company's (the request is company-scoped).
 */
export function todayCounts(q: Queue | null, o: { company: string; draftable: boolean; rows: readonly { company: string; state: StateKey | null }[]; reviews: readonly QueueReview[] }): TodayCounts | null {
  if (!q) return null;
  const t = q.totals;
  const loadedActions = o.rows.filter((r) => (!o.company || r.company === o.company) && isAction(r.state, o.draftable)).length;
  if (!t) return { actions: loadedActions, reviews: o.reviews.filter((r) => !o.company || r.company === o.company).length, waiting: o.rows.length - loadedActions };
  if (!o.company) {
    const actions = t.needs_you + (o.draftable ? t.by_display_status.spec_approved ?? 0 : 0);
    return { actions, reviews: t.open_review_items, waiting: Math.max(0, t.assignments - actions) };
  }
  const c = t.by_company?.find((x) => x.company === o.company);
  const drafts = o.draftable ? o.rows.filter((r) => r.company === o.company && r.state === "specOk").length : 0;
  const actions = (c?.needs_you ?? 0) + drafts;
  return { actions, reviews: c?.open_review_items ?? 0, waiting: Math.max(0, (c?.assignments ?? 0) - actions) };
}

/**
 * AIC-433: an invite drafted SEND_UNDETECTED_DAYS or more ago that still isn't detected as sent: the row's flag (shown
 * like "2 days late") and a line pointing to Record it as sent on the assignment. Null for any other row, or one this page
 * just changed. `readOnly`: someone else's queue or a preview, so the line doesn't say "you".
 */
export function unsentFlag(r: { status: string; state: StateKey | null; draft_created_at?: string | null; just?: boolean }, readOnly: boolean, now: Date = new Date()): { flag: string; line: string } | null {
  if (r.state !== "drafted" || r.just) return null;
  const days = sendUndetectedDays(r, now);
  if (days === null) return null;
  return {
    flag: "Not detected as sent",
    line: `Drafted ${days} ${plural(days, "day", "days")} ago and not detected as sent. ${readOnly ? "If it was sent, it can be recorded as sent on the assignment." : "If you sent it, use Record it as sent on the assignment."}`,
  };
}

/** Why an assignment action is the suggested next step (the prototype's reasons). */
export const ASSIGNMENT_REASON: Partial<Record<StateKey, string>> = {
  specReady: "Approve the questions and estimates before creating an invitation.",
  specOk: "The spec is approved, so the invitation draft is ready to create.",
  drafted: "The draft is in your Outlook Drafts. Review it there; you send it.",
  follow: "Some figures need another answer from the vendor.",
  results: "No open review item holds these results. You can review them now.",
};
